import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL } from '../config/api';
import '../styles/CalendarManagement.css';

interface CalendarSettings {
  enabled: boolean;
  colorId: string;
  title: string;
  description: string;
  includeWhatsapp: boolean;
}

const colorOptions = [
  { id: '1', name: 'أزرق فاتح', color: '#a4bdfc' },
  { id: '2', name: 'أخضر', color: '#7ae7bf' },
  { id: '3', name: 'بنفسجي', color: '#dbadff' },
  { id: '4', name: 'وردي', color: '#ff887c' },
  { id: '5', name: 'أصفر', color: '#fbd75b' },
  { id: '6', name: 'برتقالي', color: '#ffb878' },
  { id: '7', name: 'فيروزي', color: '#46d6db' },
  { id: '8', name: 'رمادي', color: '#e1e1e1' },
  { id: '9', name: 'أزرق', color: '#5484ed' },
  { id: '10', name: 'أخضر داكن', color: '#51b749' },
  { id: '11', name: 'أحمر', color: '#dc2127' }
];

function CalendarManagement() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<CalendarSettings>({
    enabled: true,
    colorId: '7',
    title: 'فوتوميستا - {customerName}',
    description: 'رقم الحجز: {bookingCode}\nنوع البوث: {boothType}\nنوع الحجز: {bookingType}\nرقم الجوال: {phone}\nالبريد الإلكتروني: {email}\nواتساب العميل: {whatsappLink}',
    includeWhatsapp: true
  });
  
  const [loading, setLoading] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState<any>(null);
  const [editingField, setEditingField] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('fotomista_token');
    if (!token) {
      navigate('/admin-login');
    }
    
    fetchCalendarSettings();
    checkCalendarStatus();
  }, [navigate]);

  const fetchCalendarSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/calendar/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSettings(data.settings);
        }
      }
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/calendar/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCalendarStatus(data);
      }
    } catch (error) {
      console.error('Error checking calendar status:', error);
    }
  };

  const handleToggleEnabled = async () => {
    setLoading(true);
    try {
      const newSettings = { ...settings, enabled: !settings.enabled };
      
      const response = await fetch(`${API_BASE_URL}/calendar/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        setSettings(newSettings);
        alert(newSettings.enabled ? 'تم تفعيل Google Calendar' : 'تم تعطيل Google Calendar');
      }
    } catch (error) {
      console.error('Error toggling calendar:', error);
      alert('حدث خطأ في تحديث الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/calendar/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token')}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setEditingField(null);
        alert('تم حفظ الإعدادات بنجاح');
      } else {
        alert('حدث خطأ في حفظ الإعدادات');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('حدث خطأ في حفظ الإعدادات');
    } finally {
      setLoading(false);
    }
  };


  const getVariableHelp = () => {
    return (
      <div className="variable-help">
        <h4>المتغيرات المتاحة:</h4>
        <div className="variables-grid">
          <span className="variable-tag">{'{customerName}'} - اسم العميل</span>
          <span className="variable-tag">{'{bookingCode}'} - رقم الحجز</span>
          <span className="variable-tag">{'{boothType}'} - نوع البوث (كلاسيك/مودرن)</span>
          <span className="variable-tag">{'{bookingType}'} - نوع الحجز (ساعات/تذاكر)</span>
          <span className="variable-tag">{'{phone}'} - رقم الجوال</span>
          <span className="variable-tag">{'{email}'} - البريد الإلكتروني</span>
          <span className="variable-tag">{'{eventDate}'} - تاريخ المناسبة</span>
          <span className="variable-tag">{'{eventTime}'} - وقت المناسبة</span>
          <span className="variable-tag">{'{location}'} - الموقع</span>
          <span className="variable-tag">{'{totalAmount}'} - المبلغ الإجمالي</span>
          <span className="variable-tag">{'{whatsappLink}'} - رابط الواتساب</span>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title="إدارة التقويم">
      <motion.div 
        className="calendar-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="calendar-management-container">
        {/* حالة الاتصال */}
        <div className="status-card">
          <h2>حالة الاتصال</h2>
          {calendarStatus ? (
            <div className={`status-indicator ${calendarStatus.configured ? 'connected' : 'disconnected'}`}>
              <div className="status-dot"></div>
              <span>
                {calendarStatus.configured ? 
                  `متصل - ${calendarStatus.calendarId}` : 
                  'غير متصل - يرجى إضافة API Key'
                }
              </span>
            </div>
          ) : (
            <div className="status-indicator loading">
              <span>جاري التحقق...</span>
            </div>
          )}
        </div>

        {/* تفعيل/تعطيل الخدمة */}
        <div className="settings-card">
          <div className="setting-header">
            <h2>تفعيل Google Calendar</h2>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={handleToggleEnabled}
                disabled={loading}
              />
              <span className="slider"></span>
            </label>
          </div>
          <p className="setting-description">
            عند التفعيل، سيتم إنشاء موعد تلقائياً في Google Calendar لكل حجز مؤكد
          </p>
        </div>

        {/* إعدادات الموعد */}
        {settings.enabled && (
          <div className="settings-grid">
            {/* لون الحدث */}
            <div className="setting-card">
              <h3>لون الحدث</h3>
              <div className="color-options">
                {colorOptions.map(color => (
                  <div
                    key={color.id}
                    className={`color-option ${settings.colorId === color.id ? 'selected' : ''}`}
                    style={{ backgroundColor: color.color }}
                    onClick={() => setSettings({...settings, colorId: color.id})}
                    title={color.name}
                  >
                    {settings.colorId === color.id && <span className="check-mark">✓</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* عنوان الموعد */}
            <div className="setting-card">
              <h3>عنوان الموعد</h3>
              {editingField === 'title' ? (
                <div className="edit-field">
                  <textarea
                    value={settings.title}
                    onChange={(e) => setSettings({...settings, title: e.target.value})}
                    className="field-editor"
                    rows={2}
                  />
                  <div className="edit-buttons">
                    <button onClick={handleSaveSettings} className="save-btn" disabled={loading}>
                      حفظ
                    </button>
                    <button onClick={() => setEditingField(null)} className="cancel-btn">
                      إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <div className="field-display">
                  <p className="field-preview">{settings.title}</p>
                  <button onClick={() => setEditingField('title')} className="edit-btn">
                    تحرير
                  </button>
                </div>
              )}
            </div>

            {/* وصف الموعد */}
            <div className="setting-card">
              <h3>وصف الموعد</h3>
              {editingField === 'description' ? (
                <div className="edit-field">
                  <textarea
                    value={settings.description}
                    onChange={(e) => setSettings({...settings, description: e.target.value})}
                    className="field-editor"
                    rows={6}
                  />
                  <div className="edit-buttons">
                    <button onClick={handleSaveSettings} className="save-btn" disabled={loading}>
                      حفظ
                    </button>
                    <button onClick={() => setEditingField(null)} className="cancel-btn">
                      إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <div className="field-display">
                  <pre className="field-preview">{settings.description}</pre>
                  <button onClick={() => setEditingField('description')} className="edit-btn">
                    تحرير
                  </button>
                </div>
              )}
            </div>

            {/* تضمين واتساب العميل */}
            <div className="setting-card">
              <h3>تضمين واتساب العميل</h3>
              <div className="whatsapp-setting">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.includeWhatsapp}
                    onChange={(e) => setSettings({...settings, includeWhatsapp: e.target.checked})}
                  />
                  <span>إضافة رابط واتساب العميل في وصف الحدث</span>
                </label>
                <p className="setting-description">
                  عند التفعيل، سيتم إضافة رابط مباشر لواتساب العميل في وصف الحدث
                  باستخدام رقم الجوال المسجل في الحجز
                </p>
                <div className="whatsapp-example">
                  <p>مثال على الرابط:</p>
                  <code>https://wa.me/966501234567</code>
                </div>
                <button onClick={handleSaveSettings} className="save-btn" disabled={loading}>
                  حفظ الإعداد
                </button>
              </div>
            </div>
          </div>
        )}

        {/* مساعدة المتغيرات */}
        {getVariableHelp()}

        {/* معاينة الموعد */}
        {settings.enabled && (
          <div className="preview-card">
            <h2>معاينة الموعد</h2>
            <div className="preview-content">
              <div className="preview-title">
                <strong>العنوان:</strong> {settings.title}
              </div>
              <div className="preview-description">
                <strong>الوصف:</strong>
                <pre>{settings.description}</pre>
                {settings.includeWhatsapp && (
                  <p className="whatsapp-note">
                    <span className="check-icon">✓</span> سيتم تضمين رابط واتساب العميل
                  </p>
                )}
              </div>
              <div className="preview-color">
                <strong>اللون:</strong>
                <span 
                  className="color-preview" 
                  style={{ backgroundColor: colorOptions.find(c => c.id === settings.colorId)?.color }}
                ></span>
                {colorOptions.find(c => c.id === settings.colorId)?.name}
              </div>
            </div>
          </div>
        )}

        {/* حفظ الإعدادات */}
        <div className="save-section">
          <button 
            onClick={handleSaveSettings} 
            className="save-all-btn" 
            disabled={loading}
          >
            {loading ? 'جاري الحفظ...' : 'حفظ جميع الإعدادات'}
          </button>
        </div>
        </div>
      </motion.div>
    </AdminLayout>
  );
}

export default CalendarManagement;